import React, { useEffect, useMemo } from 'react'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { usePaginateItemsWithNavigate } from 'src/components/Reusable/paginate/paginate.hooks'
import { PaginateButtonsGroup } from 'src/components/Reusable/paginate/PaginateButtons'
import { FilterBar } from 'src/components/Reusable/IndexPage/FilterBar'
import { FilterBarConfigs } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { useAnalytics } from 'src/analytics/useAnalytics'
import { InfoSection } from 'src/components/Reusable/IndexPage/InfoSection'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import {
  SecondaryButton,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons/StandardButton'
import {
  useDownloadRiskCSV,
  useRisksRequest,
  useRisks,
  useRiskComputedValues,
  useRiskCategoryOptions,
} from 'src/pages/Risks/risks.hooks'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { IndexPage } from 'src/components/Reusable/IndexPage'
import { ModalFormId } from 'src/components/ModalForms'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { useSearchParams } from 'react-router-dom'
import { AddRiskModalBtn } from '../risks.components'
import { AddRiskModal } from '../modals/AddRiskModal'
import { AddManualRiskModal } from '../modals/AddManualRiskModal'
import { AddRiskCSVModal } from '../modals/AddRiskCSVModal'
import { RisksIndexGrid } from './RisksIndexGrid'
import { useSearchableRisks } from './RiskIndexGrid.hooks'

const RISKS_PAGE_SIZE = 20

export const RisksIndexPage = (): JSX.Element => {
  const { sortRisk } = useRiskComputedValues()
  const initConfig = useRiskCategoryOptions()

  useEffect(() => {
    initConfig()
  }, [initConfig])

  const req = useRisksRequest()
  const { data, isLoading } = useRisks(req)
  const [searchParams] = useSearchParams()

  const pageCount = Number(searchParams.get('pc'))

  const totalRisks = data?.getTotal() ?? 0
  const allRisk = data?.getRisksList() ?? []

  const searchableRisks = useSearchableRisks(allRisk)
  const { data: risks } = useApplySearch(searchableRisks)
  const { paginatedItems, currentPage, nextPage, prevPage } =
    usePaginateItemsWithNavigate<Risk>({
      items: risks,
      pageSize: pageCount > 0 ? pageCount : RISKS_PAGE_SIZE,
    })
  const riskIds: string[] = useMemo(() => {
    return paginatedItems.sort(sortRisk).map((risk) => risk.getId())
  }, [paginatedItems, sortRisk])

  return (
    <IndexPage
      title="Risk Register"
      indexGrid={
        <RisksIndexGrid
          riskIds={riskIds}
          isLoading={isLoading || !data}
          totalRisks={totalRisks}
        />
      }
      filterBar={
        <FilterBar
          config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.RISKS]}
          searchBarOptions={{ withContentSearch: false }}
        />
      }
      actions={<RisksIndexPageActions />}
      pagination={
        <PaginateButtonsGroup
          totalItems={risks.length}
          pageSize={pageCount > 0 ? pageCount : RISKS_PAGE_SIZE}
          currentPage={currentPage}
          onPrevClick={prevPage}
          onNextClick={nextPage}
        />
      }
      modals={[
        <AddRiskModal key={ModalFormId.ADD_RISK} />,
        <AddManualRiskModal key={ModalFormId.ADD_MANUAL_RISK} />,
        <AddRiskCSVModal key={ModalFormId.ADD_RISK_CSV} />,
      ]}
    />
  )
}

const RisksIndexPageActions = (): JSX.Element => {
  const infoLink = HUBSPOT.RISK_REGISTER_OVERVIEW
  const downloadRiskCSV = useDownloadRiskCSV()
  const { track, events } = useAnalytics()

  return (
    <FlexRow align={FlexAlign.CENTER} justify={FlexAlign.SPACE_BETWEEN} $mt={8}>
      <InfoSection
        text="Track and manage risks to your company."
        linkText="Learn more"
        link={infoLink}
        isExternalLink
      />
      <FlexRow justify={FlexAlign.CENTER} align={FlexAlign.FLEX_START} gap={4}>
        <SecondaryButton
          onClick={downloadRiskCSV}
          text="Export"
          size={StandardButtonSize.SMALL}
        />
        <AddRiskModalBtn
          text="Add Risk"
          handleClick={() => track(events.C_ADD_MANUAL_RISK)}
        />
      </FlexRow>
    </FlexRow>
  )
}
